/**
 * Pure reducer for the agent chat — applies one SSE event to one
 * ``ChatMessage``. Lives in its own module so unit tests can import it
 * without dragging in the full ``useAgentChat`` hook (and its
 * transitive Firebase / WorkspaceContext / ChatContextProvider deps,
 * which initialise at module load and were producing intermittent CI
 * failures when the test ran in a worker that hadn't seeded the auth
 * shims).
 */

import type {
  GroundingSource,
  HistoryAttachment,
  PendingToolChoice,
  PendingToolConfirmation,
  SpreadsheetChangeCardData,
  SSEEvent,
  WireContextItem,
} from '../../services/agentChat.service';
import { toolLabel } from '../../components/features/bird/agentActivityLabels';

export type ChatActivityStatus = 'active' | 'done' | 'failed';

/**
 * A1 cell, cell range, whole-column range (`A:A`, `B:D`) or whole-row range
 * (`3:5`): matches the server's `ContextItem.selection` pattern
 * (`agent_service/schemas/chat.py`) exactly, split into four simple
 * alternatives rather than one combined regex with nested quantifiers (the
 * combined form trips `security/detect-unsafe-regex`, which this codebase
 * doesn't get to silence with a disable comment). Kept in lockstep with the
 * server pattern deliberately, and pinned by a fixture shared with the
 * server's own test (`packages/shared/fixtures/context-selection-cases.json`):
 * pydantic is strict, so a value this client lets through that the server
 * doesn't rejects the WHOLE chat request, not just the one field.
 */
const CELL_PATTERN = /^[A-Z]{1,3}[0-9]{1,7}$/;
const CELL_RANGE_PATTERN = /^[A-Z]{1,3}[0-9]{1,7}:[A-Z]{1,3}[0-9]{1,7}$/;
const COLUMN_RANGE_PATTERN = /^[A-Z]{1,3}:[A-Z]{1,3}$/;
const ROW_RANGE_PATTERN = /^[0-9]{1,7}:[0-9]{1,7}$/;

function isValidSelection(value: string): boolean {
  return (
    CELL_PATTERN.test(value) ||
    CELL_RANGE_PATTERN.test(value) ||
    COLUMN_RANGE_PATTERN.test(value) ||
    ROW_RANGE_PATTERN.test(value)
  );
}

const MAX_SHEET_NAME_LENGTH = 100;

/** Reads a context's sheet/selection detail, never letting it break the send. */
function readDetail(
  detail: (() => { sheet?: string; selection?: string }) | undefined,
): { sheet?: string; selection?: string } | undefined {
  if (!detail) return undefined;
  try {
    return detail();
  } catch {
    // A throwing getter (e.g. the grid unmounted mid-send) loses the extra
    // coordinates, never the message.
    return undefined;
  }
}

/**
 * Sends the RAW sheet name, not a trimmed one: it's a hint the agent uses to
 * find the tab by its real name, and a name with meaningful leading/trailing
 * spaces (`" Data "`) would no longer match the tab if we trimmed it. `trim()`
 * is used only to detect a whitespace-only name; the length check runs on the
 * untrimmed value, matching the server's own `min_length`/`max_length` on the
 * raw field.
 */
/**
 * Exported so `useLiveViewContext` (the live socket's `view_context` uplink)
 * validates a spreadsheet's sheet/selection identically to chat's own
 * `toContextItems` below: one grammar, matched to the server's, not two
 * copies that can drift apart.
 */
export function validSheet(sheet: string | undefined): string | undefined {
  if (!sheet || !sheet.trim() || sheet.length > MAX_SHEET_NAME_LENGTH) return undefined;
  return sheet;
}

export function validSelection(selection: string | undefined): string | undefined {
  if (!selection) return undefined;
  // Univer's own `getA1Notation` only emits `$` when an absolute ref type is
  // requested explicitly, which the grid never does. The server's pattern
  // rejects `$` outright, though, so a stray absolute marker from any future
  // caller is stripped rather than silently dropping the whole selection.
  const stripped = selection.replace(/\$/g, '');
  return isValidSelection(stripped) ? stripped : undefined;
}

/**
 * A context's sheet/selection exactly as `toContextItems` would send them:
 * read (never throwing), then validated. Exported so the composer's context
 * chip shows the same values the next send carries, not a second reading that
 * could disagree with it.
 */
export function readValidDetail(
  detail: (() => { sheet?: string; selection?: string }) | undefined,
): { sheet?: string; selection?: string } {
  const raw = readDetail(detail);
  return { sheet: validSheet(raw?.sheet), selection: validSelection(raw?.selection) };
}

/**
 * Map the composer's context entities onto the wire shape.
 *
 * Pure + exported so the single→many migration has a regression guard that
 * doesn't drag the hook's Firebase/workspace graph into the test runner. An
 * empty selection sends `undefined`, NOT `[]` — the agent treats an omitted
 * field as "no context supplied" and an empty array as "explicitly none".
 *
 * `detail` is called HERE, at send time, not when the context was attached:
 * a spreadsheet's sheet and selection can change between the two. An invalid
 * `sheet` or `selection` is omitted rather than sent: the server validates
 * `ContextItem` with strict pydantic, so one bad field would reject the
 * entire chat request, not just drop that field.
 */
export function toContextItems(
  contexts:
    | readonly {
        type: string;
        id: string;
        name?: string;
        detail?: () => { sheet?: string; selection?: string };
      }[]
    | undefined,
): WireContextItem[] | undefined {
  if (!contexts?.length) return undefined;
  return contexts.map((c) => {
    const { sheet, selection } = readValidDetail(c.detail);
    return {
      type: c.type,
      id: c.id,
      name: c.name || c.type,
      ...(sheet ? { sheet } : {}),
      ...(selection ? { selection } : {}),
    };
  });
}

export interface ChatActivity {
  readonly id: string;
  readonly kind: 'tool_call' | 'tool_result';
  readonly tool: string;
  readonly label: string;
  readonly status: ChatActivityStatus;
}

export interface ChatMessage {
  id: string;
  role: 'user' | 'assistant' | 'status';
  content: string;
  author?: string;
  timestamp: number;
  sources?: GroundingSource[];
  traceId?: string;
  attachments?: HistoryAttachment[];
  activities?: ChatActivity[];
  /**
   * Tool calls paused awaiting the researcher's approval, each rendering its
   * own Confirm/Cancel control. Entries are removed as they are answered.
   *
   * A LIST because one turn can pause more than one call: ADK raises an
   * `adk_request_confirmation` per paused call, and the writer holds two
   * confirmation tools (`merge_artifacts`, `create_visual_reference_set`), so
   * "tidy these notes and build a reference set" pauses both. A single slot
   * silently kept only the last, leaving the other unanswerable while the
   * model had already been told both were pending — one destructive op runs,
   * the other never does, and nothing says so (rule 12).
   */
  pendingConfirmations?: PendingToolConfirmation[];
  /**
   * Questions paused awaiting the researcher's pick, each rendering its own
   * option list. A LIST for the same reason as `pendingConfirmations`: one
   * turn can raise more than one, and a single slot would silently keep only
   * the last while the model believed both were outstanding.
   */
  pendingChoices?: PendingToolChoice[];
  /**
   * One card per `apply_spreadsheet_ops` call this turn made, in order. Built
   * from the tool result, never from the text (P2-D4). A LIST because the
   * specialist can apply more than one batch in a turn.
   */
  spreadsheetChanges?: SpreadsheetChangeCardData[];
  /**
   * Client-local lifecycle for a newly submitted assistant turn. History rows
   * intentionally omit it, so stored messages cannot be mistaken for the
   * current onboarding turn.
   */
  deliveryStatus?: 'streaming' | 'done' | 'error' | 'cancelled';
  /**
   * The user cancelled this assistant turn. Once true, the reducer
   * stops appending content from late SSE events for this message —
   * the chat shows whatever partial text arrived before the cancel
   * plus an explicit "_[cancelled]_" suffix.
   */
  cancelled?: boolean;
  /**
   * Present only on ``role === 'status'`` rows — an inline notice that a
   * chat-spawned background job finished or failed (author_long_document,
   * equipment knowledge fetch). Rendered as a quiet status line, not a bubble.
   * `info` is neither outcome: a send turned away because the session was
   * still running an earlier turn (`refuseTurn` in `agentChatTurnEvents.ts`).
   */
  statusKind?: 'success' | 'error' | 'info';
  /** Entity the status refers to, for an inline "Open" affordance. */
  statusRef?: { type: string; id: string; name?: string };
}

/** The calm status line a refused send leaves (`refuseTurn`). */
export function isRefusalNotice(message: ChatMessage): boolean {
  return message.role === 'status' && message.statusKind === 'info';
}

/**
 * Mark the in-flight assistant message as cancelled. Pure helper called
 * by `useAgentChat.cancelMessage` — extracted so the bug class it fixes
 * (capture id BEFORE clearing the ref) has a unit-level regression
 * guard. See PR-148 review.
 *
 * Returns a NEW messages array — never mutates the input. Appends
 * "_[cancelled]_" so the chat shows the cancellation explicitly. If the
 * target message can't be found (e.g. ref pointed at a stale id), the
 * input array is returned unchanged.
 */
export function applyCancelMarker(
  messages: ReadonlyArray<ChatMessage>,
  inFlightId: string | null,
): ChatMessage[] {
  if (!inFlightId) return [...messages];
  return messages.map((m) =>
    m.id === inFlightId && m.role === 'assistant'
      ? {
          ...m,
          cancelled: true,
          deliveryStatus: 'cancelled',
          content: m.content ? `${m.content}\n\n_[cancelled]_` : '_[cancelled]_',
        }
      : m,
  );
}

/**
 * Append a tool call to the activity strip, or resolve one already there.
 *
 * Extracted from the reducer switch alongside `applyConfirmation` — between
 * them the two cases carried most of its branching, and adding the
 * confirmation case pushed the function past the complexity ceiling.
 */
function applyActivity(msg: ChatMessage, event: SSEEvent): ChatMessage {
  const { id, kind, tool, label, ok } = event.data;
  if (!id || !kind || !tool) return msg;
  const activities = msg.activities ?? [];
  if (kind === 'tool_call') {
    if (activities.some((a) => a.id === id)) return msg;
    const next: ChatActivity = {
      id,
      kind: 'tool_call',
      tool,
      label: label ?? toolLabel(tool),
      status: 'active',
    };
    return { ...msg, activities: [...activities, next] };
  }
  return {
    ...msg,
    activities: activities.map((a) =>
      a.id === id ? { ...a, status: ok === false ? 'failed' : 'done' } : a,
    ),
  };
}

/**
 * Answer a paused tool call: send the reply, and clear the dialog only if the
 * reply actually went.
 *
 * Extracted from `useAgentChat` because the ORDER is the whole behaviour, and
 * order is not assertable from inside the hook (this module is deliberately
 * Firebase-free so its tests can run without the auth shims). Clearing first
 * and sending second looks identical in review and fails silently in use:
 * `send` declines while another turn is streaming — exactly what happens when
 * the researcher types something else and then answers the older dialog — so
 * the buttons vanish and nothing is merged. That is the silent-success class
 * (rule 12) this whole change exists to remove, reintroduced on the client.
 *
 * Returns whether the reply was sent.
 */
export async function respondToPendingConfirmation(
  callId: string,
  confirmed: boolean,
  deps: {
    readonly send: (reply: { callId: string; confirmed: boolean }) => Promise<unknown | null>;
    readonly clear: (callId: string) => void;
    /** Told when the reply never went, so the click is never silent. */
    readonly onDeclined?: () => void;
  },
): Promise<boolean> {
  const outcome = await deps.send({ callId, confirmed });
  if (outcome === null) {
    // Leave the dialog up AND say so. Silently leaving it up is better than
    // silently clearing it, but the researcher still pressed a button and is
    // owed an answer.
    deps.onDeclined?.();
    return false;
  }
  deps.clear(callId);
  return true;
}

/**
 * Send one choice answer, and clear it only once the reply is actually away.
 *
 * Sibling of `respondToPendingConfirmation`, same ordering contract and the
 * same reason for it: clearing first would leave a researcher who hit a failed
 * send with no control and no idea the pick never landed.
 */
export async function respondToPendingChoice(
  callId: string,
  value: string,
  deps: {
    readonly send: (reply: { callId: string; value: string }) => Promise<unknown | null>;
    readonly clear: (callId: string) => void;
    /** Told when the reply never went, so the click is never silent. */
    readonly onDeclined?: () => void;
  },
): Promise<boolean> {
  const outcome = await deps.send({ callId, value });
  if (outcome === null) {
    deps.onDeclined?.();
    return false;
  }
  deps.clear(callId);
  return true;
}

/**
 * Remove one answered question, retiring a carrier row left with nothing to say.
 *
 * Shares `PENDING_CONFIRMATION_CARRIER_PREFIX` with the confirmation path
 * because a replayed carrier can hold BOTH kinds: `attachPendingConfirmations`
 * mints one row for whatever the server says is still paused. Retiring it
 * therefore has to check both lists, or answering the last question on a row
 * that also held a confirmation would delete a live prompt.
 */
export function clearPendingChoice(messages: ChatMessage[], callId: string): ChatMessage[] {
  return messages
    .map((m) =>
      m.pendingChoices?.some((c) => c.id === callId)
        ? { ...m, pendingChoices: m.pendingChoices.filter((c) => c.id !== callId) }
        : m,
    )
    .filter(
      (m) =>
        !m.id.startsWith(PENDING_CONFIRMATION_CARRIER_PREFIX) ||
        !!m.pendingConfirmations?.length ||
        !!m.pendingChoices?.length,
    );
}

/**
 * Id prefix of a message that exists ONLY to carry a replayed confirmation.
 *
 * Declared once because two places must agree on it: `attachPendingConfirmations`
 * mints the row, and `clearPendingConfirmation` retires it. A literal in each
 * would let one change and strand an empty row in the transcript forever.
 */
export const PENDING_CONFIRMATION_CARRIER_PREFIX = 'pending-confirmation-';

/**
 * Remove one answered call, and retire a carrier row left with nothing to say.
 *
 * Extracted from `useAgentChat` when replay started minting carrier rows. The
 * removal alone would leave one behind as an empty gap in the transcript: it
 * has no text by design, so once its last prompt is answered there is nothing
 * left to render.
 *
 * ONLY a carrier is retired, never any assistant message that happens to be
 * empty — `sendMessage` creates the next turn's message with empty content
 * before the first token lands, and this runs immediately after the reply is
 * away, so a general "drop empty assistant messages" rule would delete the
 * turn the researcher is waiting on.
 */
export function clearPendingConfirmation(messages: ChatMessage[], callId: string): ChatMessage[] {
  return messages
    .map((m) =>
      m.pendingConfirmations?.some((c) => c.id === callId)
        ? { ...m, pendingConfirmations: m.pendingConfirmations.filter((c) => c.id !== callId) }
        : m,
    )
    .filter(
      (m) =>
        !m.id.startsWith(PENDING_CONFIRMATION_CARRIER_PREFIX) ||
        !!m.pendingConfirmations?.length ||
        // A replayed carrier can hold both kinds. Without this, answering the
        // last confirmation on a shared row would take a live question with it.
        !!m.pendingChoices?.length,
    );
}

/**
 * Re-attach the calls the server says are STILL paused, after a history load.
 *
 * A confirmation is not a message — ADK stores it as a function call with no
 * text — so the transcript replay rebuilds it out of existence. Live,
 * `applyConfirmation` folds each pause into the turn that raised it; on reload
 * there is no such turn to fold into, so placement is decided here.
 *
 * The control goes on the LAST message when that message is Bird's, and on a
 * carrier message appended to the end when it is not (the model can pause
 * without writing a word first, which would otherwise leave the prompt sitting
 * above the request that caused it). Both land it at the bottom of the
 * transcript, which is where a researcher who has just reloaded is looking and
 * where an unanswered destructive prompt should be.
 *
 * That placement is deliberately not a reconstruction of which turn each pause
 * came from. A session can hold pauses from two different turns — ask to merge,
 * ignore the prompt, ask for something else that also pauses — and both render
 * together at the end rather than each under its own answer. Nothing is
 * mis-ANSWERED by that: ADK pairs a reply to its call by id, so the button
 * always acts on the call it belongs to.
 *
 * Legibility is a weaker guarantee, and worth stating honestly. `merge_artifacts`
 * and `delete_artifact` name their own consequence and read fine anywhere
 * ("Merge 3 notes?"). `create_visual_reference_set` does not — its copy says
 * "the images in the preview above", and after a replay that preview may be
 * several turns up. The reply is still correct; the sentence is just vaguer
 * than it was live. Fixing that means either per-turn anchoring or copy that
 * stands alone, and the second is the cheaper answer if it starts to matter.
 *
 * Deduped by id so a pause already on screen from the live turn is not
 * rendered twice by a background refetch.
 */
/**
 * Stamp a prompt as having arrived via replay rather than live SSE.
 *
 * Client-local and set nowhere else — the server cannot know, since the same
 * payload serves both routes in. It exists so
 * `CHAT_TOOL_CONFIRMATION_DISPLAYED` stays interpretable: a prompt nobody
 * answers is now re-displayed on every reload, and without this the
 * displayed→answered rate silently becomes a function of how often people
 * reopen the panel.
 */
function asReplayed(confirmation: PendingToolConfirmation): PendingToolConfirmation {
  return { ...confirmation, replayed: true };
}

function asReplayedChoice(choice: PendingToolChoice): PendingToolChoice {
  return { ...choice, replayed: true };
}

export function attachPendingConfirmations(
  messages: ChatMessage[],
  pending: readonly PendingToolConfirmation[],
): ChatMessage[] {
  if (!pending.length) return messages;

  const last = messages[messages.length - 1];
  if (last?.role === 'assistant') {
    const existing = last.pendingConfirmations ?? [];
    const fresh = pending.filter((p) => !existing.some((e) => e.id === p.id));
    if (!fresh.length) return messages;
    return [
      ...messages.slice(0, -1),
      { ...last, pendingConfirmations: [...existing, ...fresh.map(asReplayed)] },
    ];
  }

  return [
    ...messages,
    {
      // Keyed on the first paused call so the row is stable across reloads —
      // an index-based id would move the moment another pause resolves. The
      // prefix is also what lets `clearPendingConfirmation` recognise the row
      // as a carrier and retire it once it is empty.
      id: `${PENDING_CONFIRMATION_CARRIER_PREFIX}${pending[0].id}`,
      role: 'assistant',
      // No content. The prompt speaks for itself, and inventing a sentence
      // here would put words in Bird's mouth that it never said.
      content: '',
      timestamp: last?.timestamp ?? Date.now(),
      pendingConfirmations: pending.map(asReplayed),
    },
  ];
}

/**
 * Re-attach the questions the server says are STILL paused, after a history load.
 *
 * The choice half of `attachPendingConfirmations`, and it must run AFTER it —
 * that function decides whether the last row is Bird's or a carrier must be
 * minted, and this one appends into whatever it produced. Running them the
 * other way round mints two carriers for one reload.
 *
 * Placement follows the same rule and carries the same honest limitation: both
 * kinds land at the bottom of the transcript rather than under the turn that
 * raised them. Nothing is mis-ANSWERED by that, because ADK pairs a reply to
 * its call by id.
 */
export function attachPendingChoices(
  messages: ChatMessage[],
  // Optional because a server mid-rolling-deploy omits the field entirely.
  // `getSessionHistory` already defaults it, so this is belt-and-braces for
  // any caller that does not go through it.
  pending: readonly PendingToolChoice[] | undefined,
): ChatMessage[] {
  if (!pending?.length) return messages;

  const last = messages[messages.length - 1];
  if (last?.role === 'assistant') {
    const existing = last.pendingChoices ?? [];
    const fresh = pending.filter((p) => !existing.some((e) => e.id === p.id));
    if (!fresh.length) return messages;
    return [
      ...messages.slice(0, -1),
      { ...last, pendingChoices: [...existing, ...fresh.map(asReplayedChoice)] },
    ];
  }

  return [
    ...messages,
    {
      id: `${PENDING_CONFIRMATION_CARRIER_PREFIX}${pending[0].id}`,
      role: 'assistant',
      content: '',
      timestamp: last?.timestamp ?? Date.now(),
      pendingChoices: pending.map(asReplayedChoice),
    },
  ];
}

/**
 * Attach a paused tool call to the message, or drop the event.
 *
 * Extracted from the reducer switch to keep it under the complexity ceiling —
 * and because the drop condition deserves its own explanation rather than a
 * comment inside a `case`.
 */
function applyConfirmation(msg: ChatMessage, event: SSEEvent): ChatMessage {
  const { id, tool, args, hint } = event.data;
  // Without an id there is nothing to echo back, so the reply could never be
  // paired with the paused call — drop it rather than render a button that
  // cannot work.
  if (!id || !tool) return msg;
  const pending = msg.pendingConfirmations ?? [];
  // Append, never replace: each frame is a DIFFERENT paused call. Deduped by
  // id so a redelivered frame doesn't render the same dialog twice.
  if (pending.some((c) => c.id === id)) return msg;
  return {
    ...msg,
    pendingConfirmations: [...pending, { id, tool, args: args ?? {}, hint: hint ?? '' }],
  };
}

/**
 * Append a `spreadsheet_changes` card. The server already validated it against
 * the shared result schema; this only refuses a frame too malformed to render
 * (no artifact to link, an unknown status, no change list) rather than drawing
 * a card that says something the tool did not.
 */
function applySpreadsheetChanges(msg: ChatMessage, event: SSEEvent): ChatMessage {
  const { artifactId, status, summary, changes, undoToken } = event.data;
  if (!artifactId || (status !== 'applied' && status !== 'failed') || !Array.isArray(changes)) {
    return msg;
  }
  const card: SpreadsheetChangeCardData = {
    artifactId,
    status,
    summary: summary ?? '',
    changes,
    ...(undoToken ? { undoToken } : {}),
  };
  return { ...msg, spreadsheetChanges: [...(msg.spreadsheetChanges ?? []), card] };
}

/**
 * Record a `spreadsheet_undo` outcome on the card it names. The agent's undo
 * tool answers for a change made in ANY earlier turn, so this searches the
 * whole conversation rather than the message the frame arrived on, matching
 * on the artifact and the undo token together. `outcome` is kept as sent, any
 * code at all: the card reads a code it does not know as a generic refusal. The latest frame for a token wins; a
 * frame naming no card on screen changes nothing (the same array comes back,
 * so React skips the render).
 */
export function applySpreadsheetUndo(messages: ChatMessage[], event: SSEEvent): ChatMessage[] {
  const { artifactId, undoToken, outcome } = event.data;
  if (!artifactId || !undoToken || !outcome) return messages;
  // Both, so a mismatched pair can never put one change's outcome on another card.
  const matches = (card: SpreadsheetChangeCardData) =>
    card.artifactId === artifactId && card.undoToken === undoToken;
  if (!messages.some((m) => m.spreadsheetChanges?.some(matches))) return messages;
  return messages.map((msg) =>
    msg.spreadsheetChanges?.some(matches)
      ? {
          ...msg,
          spreadsheetChanges: msg.spreadsheetChanges.map((card) =>
            matches(card) ? { ...card, undoOutcome: outcome } : card,
          ),
        }
      : msg,
  );
}

function applyChoice(msg: ChatMessage, event: SSEEvent): ChatMessage {
  const { id, message, options } = event.data;
  // Without an id the answer could never be paired with the paused call, and
  // without a message there is no question to put — either way, rendering a
  // control would be worse than rendering nothing.
  if (!id || !message) return msg;
  const pending = msg.pendingChoices ?? [];
  // Append, never replace, deduped by id — same contract as applyConfirmation.
  if (pending.some((c) => c.id === id)) return msg;
  return {
    ...msg,
    pendingChoices: [...pending, { id, message, options: options ?? [] }],
  };
}

/**
 * Which layer owns each SSE event type.
 *
 *  - `reducer`  — the fold in `reduceChatMessage` is the ENTIRE effect, so
 *                 `useAgentChat`'s dispatch forwards it and returns.
 *  - `dispatch` — needs side effects (analytics, cache invalidation, stream
 *                 teardown) alongside or instead of the fold, so it gets its
 *                 own `switch` branch there. Moving one of these into the
 *                 `reducer` group would make the dispatch return early and
 *                 silently skip that work.
 *
 * `satisfies Record<SSEEvent['type'], …>` is the load-bearing part: it makes
 * this table EXHAUSTIVE at compile time, so adding a member to
 * `SSEEvent['type']` without classifying it here fails `tsc` rather than
 * shipping an event that no layer handles.
 *
 * That is the shape of the bug this replaces. In #1619 the reducer grew a
 * `case 'confirmation'` while the dispatch's hand-written
 * `type === 'text' || …` condition did not, so every frame was parsed and then
 * dropped and `applyConfirmation` was dead code on the live path — Bird told
 * researchers to "approve the confirmation prompt" for a prompt that never
 * rendered, silently disabling merge_artifacts, delete_artifact and
 * create_visual_reference_set. A hand-maintained list in two places is what
 * allowed it; a derived, exhaustive one is what forecloses it.
 */
export const SSE_EVENT_OWNER = {
  text: 'reducer',
  sources: 'reducer',
  activity: 'reducer',
  confirmation: 'reducer',
  choice: 'reducer',
  spreadsheet_changes: 'reducer',
  // Cross-message (the card can be on any earlier turn) and remounts the grid.
  spreadsheet_undo: 'dispatch',
  agent_transfer: 'dispatch',
  done: 'dispatch',
  error: 'dispatch',
} as const satisfies Record<SSEEvent['type'], 'reducer' | 'dispatch'>;

/** The `reducer`-owned half of {@link SSE_EVENT_OWNER}, as the dispatch's gate. */
export const REDUCER_ONLY_EVENT_TYPES: ReadonlySet<SSEEvent['type']> = new Set(
  (Object.keys(SSE_EVENT_OWNER) as (keyof typeof SSE_EVENT_OWNER)[]).filter(
    (type) => SSE_EVENT_OWNER[type] === 'reducer',
  ),
);

/**
 * The `dispatch`-owned half — each of these must have its own `switch` branch.
 *
 * @internal Test seam: the dispatch-coverage tests assert each type has its own branch.
 */
export const DISPATCH_OWNED_EVENT_TYPES: ReadonlySet<SSEEvent['type']> = new Set(
  (Object.keys(SSE_EVENT_OWNER) as (keyof typeof SSE_EVENT_OWNER)[]).filter(
    (type) => SSE_EVENT_OWNER[type] === 'dispatch',
  ),
);

export function reduceChatMessage(msg: ChatMessage, event: SSEEvent): ChatMessage {
  // Once cancelled, all subsequent events for this message are dropped.
  // The fetch is already aborted; this is belt-and-braces in case any
  // in-flight buffered event slips through after the user hit stop.
  if (msg.cancelled) return msg;
  switch (event.type) {
    case 'text': {
      const incoming = event.data.content ?? '';
      return {
        ...msg,
        content: [msg.content, incoming].filter(Boolean).join('\n\n'),
        author: event.data.author ?? msg.author,
      };
    }
    case 'sources': {
      const incoming = event.data.sources ?? [];
      if (!incoming.length) return msg;
      const existing = msg.sources ?? [];
      const merged = [
        ...existing,
        ...incoming.filter((s) => !existing.some((e) => e.url === s.url)),
      ];
      return { ...msg, sources: merged };
    }
    case 'activity':
      return applyActivity(msg, event);
    case 'confirmation':
      return applyConfirmation(msg, event);
    case 'choice':
      return applyChoice(msg, event);
    case 'spreadsheet_changes':
      return applySpreadsheetChanges(msg, event);
    case 'done':
      return {
        ...msg,
        deliveryStatus: 'done',
        traceId: event.data.trace_id ?? msg.traceId,
      };
    case 'error':
      return {
        ...msg,
        deliveryStatus: 'error',
      };
    default:
      return msg;
  }
}

/**
 * Minimal shape of a backend ``/notifications`` event that a chat-spawned job
 * emits on completion/failure. ``data.conversationId`` ties it back to the
 * chat thread that started the job.
 */
export interface JobOutcomeNotification {
  type: string;
  message: string;
  timestamp?: string;
  entityRef?: { type: string; id: string; name?: string };
  data?: { conversationId?: string } & Record<string, unknown>;
}

const STATUS_KIND_BY_TYPE: Readonly<Record<string, 'success' | 'error'>> = {
  'import:note-created': 'success',
  'import:note-failed': 'error',
  'job:complete': 'success',
  'job:error': 'error',
};

/**
 * Build an inline status ``ChatMessage`` from a job-outcome notification, or
 * ``null`` when the notification isn't a surfaced job outcome. Pure — the
 * caller injects ``id`` + ``timestamp`` so ordering stays under the hook's
 * control and the unit test is deterministic.
 */
export function statusMessageFromNotification(
  event: JobOutcomeNotification,
  id: string,
  timestamp: number,
): ChatMessage | null {
  const statusKind = STATUS_KIND_BY_TYPE[event.type];
  if (!statusKind) return null;
  return {
    id,
    role: 'status',
    content: event.message,
    timestamp,
    statusKind,
    statusRef: event.entityRef,
  };
}
